import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { Product } from '../contexts/AuthContext';
import { usePermission } from '../shared/hooks/usePermission';
import { getVisibleProductsForOrgUser } from '../shared/permissions';
import { AddProductModal } from './AddProductModal/AddProductModal';
import { FeatureHint } from './FeatureHint/FeatureHint';
import { productCompactDisplayLabel } from '../shared/utils/productDisplayLabel';

function productDisplayName(p: Product): string {
  const label = productCompactDisplayLabel(p);
  return p.is_default ? `${label} (Default)` : label;
}

/** Product selector for org members; visibility rules follow RBAC plan. */
export function ProductSwitcher() {
  const { currentOrg, currentProduct, selectProduct, refreshUser, user } = useAuth();
  const { allowed: canCreateProduct } = usePermission('product.create');

  const [open, setOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const visibleProducts = useMemo(
    () => getVisibleProductsForOrgUser<Product>(currentOrg),
    [currentOrg]
  );

  const current = useMemo(() => {
    if (!visibleProducts.length) return null;
    if (currentProduct && visibleProducts.some((p) => p.id === currentProduct.id)) {
      return currentProduct;
    }
    return visibleProducts[0];
  }, [visibleProducts, currentProduct]);

  useEffect(() => {
    if (!open) return;
    const idx = current ? visibleProducts.findIndex((p) => p.id === current.id) : 0;
    setHighlightIndex(idx >= 0 ? idx : 0);
  }, [open, current, visibleProducts]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = containerRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, visibleProducts.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && open) {
        e.preventDefault();
        const p = visibleProducts[highlightIndex];
        if (p) {
          selectProduct(p);
          setOpen(false);
          triggerRef.current?.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, visibleProducts, highlightIndex, selectProduct]);

  const onSelectProduct = useCallback(
    (p: Product) => {
      selectProduct(p);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [selectProduct]
  );

  const onCreated = useCallback(
    async (product: Product) => {
      await refreshUser();
      selectProduct(product);
    },
    [refreshUser, selectProduct]
  );

  if (!currentOrg || !visibleProducts.length) return null;

  if (visibleProducts.length === 1) {
    const p = visibleProducts[0];
    return (
      <div className="product-switcher-wrap">
        <div className="product-switcher product-switcher--static" title="Current product">
          <span className="product-switcher-label">{productDisplayName(p)}</span>
          {/*
           * 3-role RBAC: every `member` sees the same view-only surface — they
           * can read Analytics/Calendar/Releases/Forecast/Commitment and fire
           * Incremental refresh, but can't modify team/integrations/etc.
           * Surface that as a single "View only" badge; the per-product
           * manager/contributor distinction no longer exists.
           */}
          {currentOrg.role === 'member' ? (
            <span className="product-switcher-badge" title="Read-only access">
              View only
            </span>
          ) : null}
          {currentOrg.is_demo ? (
            <span
              className="product-switcher-badge product-switcher-badge--demo"
              title="Demo organization: anonymized copy, integrations disabled"
            >
              Demo
            </span>
          ) : null}
        </div>
        {canCreateProduct ? (
          <button
            type="button"
            className="product-switcher-add-link"
            onClick={() => setAddModalOpen(true)}
          >
            <Plus size={14} aria-hidden />
            Add product
          </button>
        ) : null}
        <AddProductModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          organizationId={currentOrg.id}
          userEmail={user?.email ?? undefined}
          onUseExistingProduct={async ({ id }) => {
            const p = currentOrg.products?.find((x) => x.id === id);
            if (p) selectProduct(p);
            await refreshUser();
          }}
          onCreated={onCreated}
        />
      </div>
    );
  }

  return (
    <>
      <FeatureHint
        hintId="product-switcher"
        title="Switch product"
        body={
          <>
            The header product sets the Jira project context for Analytics, Calendar, Releases,
            Forecast, and related settings. Switch here when you work on another product.
          </>
        }
      >
        <div className="product-switcher" ref={containerRef}>
          <button
            type="button"
            ref={triggerRef}
            id="product-switcher-trigger"
            className="product-switcher-trigger"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls="product-switcher-listbox"
            aria-label={`Current product: ${current ? productDisplayName(current) : 'Select product'}. Open product menu.`}
            onClick={() => setOpen((o) => !o)}
          >
            <span className="product-switcher-trigger-text">
              <span className="product-switcher-label">
                {current ? productDisplayName(current) : 'Select product'}
              </span>
              {currentOrg?.is_demo ? (
                <span
                  className="product-switcher-badge product-switcher-badge--demo"
                  title="Demo organization: anonymized copy, integrations disabled"
                >
                  Demo
                </span>
              ) : null}
            </span>
            <ChevronDown className="product-switcher-chevron" size={16} aria-hidden />
          </button>

          {open ? (
            <div
              id="product-switcher-listbox"
              className="product-switcher-dropdown"
              role="listbox"
              aria-activedescendant={
                visibleProducts[highlightIndex]
                  ? `product-option-${visibleProducts[highlightIndex].id}`
                  : undefined
              }
            >
              <div className="product-switcher-dropdown-inner">
                {visibleProducts.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    id={`product-option-${p.id}`}
                    aria-selected={current?.id === p.id}
                    className={
                      i === highlightIndex
                        ? 'product-switcher-option product-switcher-option--highlight'
                        : 'product-switcher-option'
                    }
                    onMouseEnter={() => setHighlightIndex(i)}
                    onClick={() => onSelectProduct(p)}
                  >
                    <span className="product-switcher-option-name">{productDisplayName(p)}</span>
                  </button>
                ))}
                {canCreateProduct ? (
                  <div className="product-switcher-dropdown-footer">
                    <button
                      type="button"
                      className="product-switcher-add-product"
                      onClick={() => {
                        setOpen(false);
                        setAddModalOpen(true);
                      }}
                    >
                      <Plus size={16} aria-hidden />
                      Add product
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </FeatureHint>

      {currentOrg ? (
        <AddProductModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          organizationId={currentOrg.id}
          userEmail={user?.email ?? undefined}
          onUseExistingProduct={async ({ id }) => {
            const p = currentOrg.products?.find((x) => x.id === id);
            if (p) selectProduct(p);
            await refreshUser();
          }}
          onCreated={onCreated}
        />
      ) : null}
    </>
  );
}
